import { Component, OnInit, inject } from '@angular/core';
import { PlatformSettingsService } from './core/services/platform-settings.service';
import { StripeService } from './shared/services/stripe.service';
import { LanguageService, type SupportedLanguage } from './i18n/language.service';
import { ThemeService } from './core/services/theme.service';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { environment } from '../environments/environment';
import type { AppEnvironment } from '../environments/environment.types';
import { AuthService } from './core/auth/auth.service';
import { ClarityService } from './core/services/clarity.service';
import { MetaPixelService } from './core/analytics/meta-pixel.service';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [IonApp, IonRouterOutlet],
  template: `
    <ion-app mode="md">
      @if (environment.envBadge) {
        <div
          class="env-badge"
          [class.env-badge--staging]="environment.envBadge === 'STAGING'"
          [attr.aria-label]="
            environment.envBadge === 'STAGING' ? 'Staging environment' : 'Development environment'
          "
        >
          {{ environment.envBadge }}
        </div>
      }
      <ion-router-outlet></ion-router-outlet>
    </ion-app>
  `,
  styles: [
    `
      .env-badge {
        position: fixed;
        top: 0;
        right: 0;
        z-index: 9999;
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.05em;
        color: #1e3a5f;
        background: #f59e0b;
        border-bottom-left-radius: 6px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
      }
      .env-badge--staging {
        color: #faf5ff;
        background: #7c3aed;
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  protected environment: AppEnvironment = environment;
  private platformSettings = inject(PlatformSettingsService);
  private stripeService = inject(StripeService);
  private authService = inject(AuthService);
  private clarityService = inject(ClarityService);
  private metaPixelService = inject(MetaPixelService);
  /** Eager init: restore language from localStorage on every load (shell routes do not inject LanguageService). */
  private _languageService = inject(LanguageService);
  /** Eager init: restore theme from localStorage on every load. */
  private _themeService = inject(ThemeService);

  ngOnInit(): void {
    this.platformSettings.load();
    this.initializeClarity();
    this.metaPixelService.init();
    this.stripeService.registerMobilePaymentReturnHandler();
    if (typeof window !== 'undefined') {
      this.applyLanguageFromUrl(window.location.href);
      void this.authService.handleAuthRedirectUrl(window.location.href);
    }
    this.registerMobileAuthRedirectHandler();
  }

  private initializeClarity(): void {
    this.clarityService.init();
  }

  private async registerMobileAuthRedirectHandler(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    App.addListener('appUrlOpen', ({ url }) => {
      this.applyLanguageFromUrl(url);
      void this.authService.handleAuthRedirectUrl(url);
    });

    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      this.applyLanguageFromUrl(launch.url);
      void this.authService.handleAuthRedirectUrl(launch.url);
    }
  }

  /**
   * Accept website/deep-link language hint via `?lang=nl|en|fr` (or hash query),
   * then persist it through LanguageService.
   */
  private applyLanguageFromUrl(rawUrl: string): void {
    const lang = this.extractLanguageFromUrl(rawUrl);
    if (!lang || this._languageService.currentLanguage() === lang) {
      return;
    }
    this._languageService.setLanguage(lang);
  }

  private extractLanguageFromUrl(rawUrl: string): SupportedLanguage | null {
    try {
      const parsed = new URL(rawUrl);
      const fromQuery = parsed.searchParams.get('lang');
      const fromHashQuery = this.extractHashLang(parsed.hash);
      const candidate = (fromQuery ?? fromHashQuery)?.toLowerCase();
      if (!candidate) {
        return null;
      }
      const isSupported = this._languageService.supportedLanguages.some((l) => l.code === candidate);
      return isSupported ? (candidate as SupportedLanguage) : null;
    } catch {
      return null;
    }
  }

  private extractHashLang(hash: string): string | null {
    if (!hash) {
      return null;
    }
    const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
    const queryIndex = trimmed.indexOf('?');
    if (queryIndex === -1) {
      return null;
    }
    const hashQuery = trimmed.slice(queryIndex + 1);
    return new URLSearchParams(hashQuery).get('lang');
  }
}
